import { z } from "zod";

// These are delivery/native quantity strings, never Float values in command identity.
export const MAX_TARGETED_SHIPMENT_LINES = 100;
export const targetedShipmentQuantitySchema = z
  .string()
  .regex(/^(?:0|[1-9]\d{0,11})(?:\.\d{1,6})?$/, "Enter a quantity with at most six decimal places.")
  .refine(value => /[1-9]/.test(value), "Quantity must be positive.")
  .transform(value => (value.includes(".") ? value.replace(/0+$/, "").replace(/\.$/, "") : value));

const uuidSchema = z.string().uuid().toLowerCase();
const locationSourceSchema = z.object({ warehouseLocationId: uuidSchema, quantity: targetedShipmentQuantitySchema }).strict();

const shipSelectionSchema = z
  .object({
    orderItemId: uuidSchema,
    quantity: targetedShipmentQuantitySchema,
    // Location quantities are native inventory units, not delivery packages.
    // The inventory owner validates their sum after the authoritative conversion.
    locationSources: z
      .array(locationSourceSchema)
      .min(1)
      .max(100)
      .refine(sources => new Set(sources.map(source => source.warehouseLocationId)).size === sources.length, {
        message: "Choose each source location at most once.",
      })
      .optional(),
  })
  .strict();

const fulfillmentSelectionSchema = z.object({ fulfillmentLineId: uuidSchema, quantity: targetedShipmentQuantitySchema }).strict();

const commonFields = {
  orderId: uuidSchema,
  idempotencyKey: uuidSchema,
  // Ship/reversal use the exact submitted instant. Delivery applies the owner's
  // org-calendar-day validation; the transport never reinterprets browser time.
  occurredAt: z.date(),
  comment: z.string().max(2000).nullable().optional(),
};

const fulfillmentSelections = z
  .array(fulfillmentSelectionSchema)
  .min(1)
  .max(MAX_TARGETED_SHIPMENT_LINES)
  .refine(selections => new Set(selections.map(selection => selection.fulfillmentLineId)).size === selections.length, {
    message: "Choose each fulfillment line at most once.",
  });

const shipCommandSchema = z
  .object({
    ...commonFields,
    action: z.literal("SHIP"),
    selections: z
      .array(shipSelectionSchema)
      .min(1)
      .max(MAX_TARGETED_SHIPMENT_LINES)
      .refine(selections => new Set(selections.map(selection => selection.orderItemId)).size === selections.length, {
        message: "Choose each order line at most once.",
      }),
    poReceivedAt: z.date().optional(),
    shipmentNo: z.string().trim().min(1).max(100).optional(),
  })
  .strict();
const deliverCommandSchema = z.object({ ...commonFields, action: z.literal("DELIVER"), selections: fulfillmentSelections }).strict();

export const targetedShipmentForwardCommandSchema = z.discriminatedUnion("action", [shipCommandSchema, deliverCommandSchema]);
export type TargetedShipmentForwardCommand = z.infer<typeof targetedShipmentForwardCommandSchema>;

export const targetedShipmentCommandSchema = z.discriminatedUnion("action", [
  shipCommandSchema,
  deliverCommandSchema,
  z.object({ ...commonFields, action: z.literal("REVERSE_SHIP"), selections: fulfillmentSelections }).strict(),
  z.object({ ...commonFields, action: z.literal("REVERSE_DELIVERY"), selections: fulfillmentSelections }).strict(),
]);

export type TargetedShipmentCommand = z.infer<typeof targetedShipmentCommandSchema>;

export const targetedShipmentAvailabilitySchema = z.object({ orderId: uuidSchema }).strict();
